import React, { useState } from 'react';
import { Row, Col, Form, FloatingLabel, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Label } from '../../../../components';
import { CarrierAddress } from '../../Model/CarrierAddresses';
import { CarrierFormMode } from './CarrierDetailsForm';

interface CarrierContactInfoSectionProps {
  mode: CarrierFormMode;
  address: CarrierAddress;
  index: number;
  onChange: (field: keyof CarrierAddress, value: string) => void;
}

const CarrierContactInfoSection: React.FC<CarrierContactInfoSectionProps> = ({ mode, address, index, onChange }) => {
  const isEditable = mode === 'edit' || mode === 'create';
  const [formValidation, setFormValidation] = useState<any>({});

  const numericOnly = (value: string) => value.replace(/[^0-9]/g, '');

  const isValidEmail = (value: string) => {
    if (!value) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleEmailChange = (value: string) => {
    onChange('eMail', value);
    setFormValidation((prev: any) => {
      if (value && !isValidEmail(value)) {
        return { ...prev, email: { isValid: false, warningText: 'Please enter a valid email address.' } };
      }
      const { email: removed, ...rest } = prev;
      return rest;
    });
  };

  const handleEmailBlur = (value: string) => {
    setFormValidation((prev: any) => {
      if (value && !isValidEmail(value)) {
        return { ...prev, email: { isValid: false, warningText: 'Please enter a valid email address.' } };
      }
      return prev;
    });
  };

  return (
    <>
      <Row className="mt-2">
        <Col md={12} className="mb-2">
          <Label text="Contact Information" className="roboto-condensed-bold" />
        </Col>
      </Row>
      <Row>
        <Col md={6} className="mb-3">
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon="phone" />
            </InputGroup.Text>
            <FloatingLabel controlId={`areaCode-${index}`} label="Area Code">
              <Form.Control
                type="tel"
                value={(address.areaCode as string) || ''}
                onChange={e => onChange('areaCode', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={3}
              />
            </FloatingLabel>
            <FloatingLabel controlId={`phone-${index}`} label="Phone">
              <Form.Control
                type="tel"
                value={(address.phone as string) || ''}
                onChange={e => onChange('phone', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={7}
              />
            </FloatingLabel>
            <FloatingLabel controlId={`ext-${index}`} label="Ext">
              <Form.Control
                type="tel"
                value={(address.ext as string) || ''}
                onChange={e => onChange('ext', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={10}
              />
            </FloatingLabel>
          </InputGroup>
        </Col>
        <Col md={6} className="mb-3">
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon="fax" />
            </InputGroup.Text>
            <FloatingLabel controlId={`faxAreaCode-${index}`} label="Fax Area Code">
              <Form.Control
                type="tel"
                value={(address.faxAreaCode as string) || ''}
                onChange={e => onChange('faxAreaCode', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={3}
              />
            </FloatingLabel>
            <FloatingLabel controlId={`faxPhone-${index}`} label="Fax">
              <Form.Control
                type="tel"
                value={(address.faxPhone as string) || ''}
                onChange={e => onChange('faxPhone', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={7}
              />
            </FloatingLabel>
          </InputGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6} className="mb-3">
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon="phone" />
            </InputGroup.Text>
            <FloatingLabel controlId={`areaCodePhone24Hr-${index}`} label="24Hr Area Code">
              <Form.Control
                type="tel"
                value={(address.areaCodePhone24Hr as string) || ''}
                onChange={e => onChange('areaCodePhone24Hr', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={3}
              />
            </FloatingLabel>
            <FloatingLabel controlId={`phone24Hr-${index}`} label="24Hr Phone">
              <Form.Control
                type="tel"
                value={(address.phone24Hr as string) || ''}
                onChange={e => onChange('phone24Hr', numericOnly(e.target.value))}
                disabled={!isEditable}
                maxLength={7}
              />
            </FloatingLabel>
          </InputGroup>
        </Col>
        <Col md={6} className="mb-3">
          <InputGroup hasValidation>
            <InputGroup.Text>
              <FontAwesomeIcon icon="envelope" />
            </InputGroup.Text>
            <FloatingLabel controlId={`email-${index}`} label="Email">
              <Form.Control
                type="email"
                value={(address.eMail as string) || ''}
                onChange={e => handleEmailChange(e.target.value)}
                onBlur={e => handleEmailBlur(e.target.value)}
                disabled={!isEditable}
                maxLength={150}
                isInvalid={formValidation['email']?.isValid === false}
              />
            </FloatingLabel>
            <Form.Control.Feedback type="invalid">
              {formValidation['email']?.warningText}
            </Form.Control.Feedback>
          </InputGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6} className="mb-3">
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon="globe" />
            </InputGroup.Text>
            <FloatingLabel controlId={`companyURL-${index}`} label="Company URL">
              <Form.Control
                type="text"
                value={(address.companyURL as string) || ''}
                onChange={e => onChange('companyURL', e.target.value)}
                disabled={!isEditable}
                maxLength={150}
              />
            </FloatingLabel>
          </InputGroup>
        </Col>
      </Row>
    </>
  );
};

export default CarrierContactInfoSection;
